import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { PeopleFlowDevice } from '@/hooks/usePeopleFlowDevice'

interface PeopleFlowDeviceResponse {
  success: boolean
  data: PeopleFlowDevice | null
  error?: {
    code: string
    message: string
  }
}

interface CreatePeopleFlowDeviceRequest {
  clinic_id: number
  name: string
  ip_address: string
  is_active: boolean
}

type UpdatePeopleFlowDeviceRequest = Partial<CreatePeopleFlowDeviceRequest>

export const peopleFlowDeviceService = {
  updatePeopleFlowDevice: async (
    id: number,
    data: UpdatePeopleFlowDeviceRequest,
    token: string,
  ): Promise<PeopleFlowDeviceResponse> => {
    try {
      const response = await apiClient.put<PeopleFlowDeviceResponse>(
        `/api/people-flow-devices/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as PeopleFlowDeviceResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'UPDATE_PEOPLE_FLOW_DEVICE_FAILED',
          message: '更新人流監控裝置失敗',
        },
      }
    }
  },

  createPeopleFlowDevice: async (
    data: CreatePeopleFlowDeviceRequest,
    token: string,
  ): Promise<PeopleFlowDeviceResponse> => {
    try {
      const response = await apiClient.post<PeopleFlowDeviceResponse>(
        '/api/people-flow-devices',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as PeopleFlowDeviceResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'CREATE_PEOPLE_FLOW_DEVICE_FAILED',
          message: '建立人流監控裝置失敗',
        },
      }
    }
  },

  deletePeopleFlowDevice: async (id: number, token: string): Promise<PeopleFlowDeviceResponse> => {
    try {
      const response = await apiClient.delete<PeopleFlowDeviceResponse>(
        `/api/people-flow-devices/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as PeopleFlowDeviceResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'DELETE_PEOPLE_FLOW_DEVICE_FAILED',
          message: '刪除人流監控裝置失敗',
        },
      }
    }
  },

  setPeopleFlowDeviceForExaminationRoom: async (
    examinationRoomId: number,
    peopleFlowDeviceId: number,
    token: string,
  ): Promise<{ success: boolean; error?: { code: string; message: string } }> => {
    try {
      const response = await apiClient.put(
        `/api/examination-room/${examinationRoomId}/people-flow-device`,
        { people_flow_device_id: peopleFlowDeviceId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as { success: boolean; error: { code: string; message: string } }
      }
      return {
        success: false,
        error: {
          code: 'SET_PEOPLE_FLOW_DEVICE_FAILED',
          message: '設置人流監控裝置失敗',
        },
      }
    }
  },

  setPeopleFlowDeviceForBroadcastDevice: async (
    broadcastDeviceId: number,
    peopleFlowDeviceId: number,
    token: string,
  ): Promise<{ success: boolean; error?: { code: string; message: string } }> => {
    try {
      const response = await apiClient.put(
        `/api/broadcast-device/${broadcastDeviceId}/people-flow-device`,
        { people_flow_device_id: peopleFlowDeviceId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as { success: boolean; error: { code: string; message: string } }
      }
      return {
        success: false,
        error: {
          code: 'SET_PEOPLE_FLOW_DEVICE_FAILED',
          message: '設置人流監控裝置失敗',
        },
      }
    }
  },
}
